import * as React from 'react'
import {
  ChartScanDetail,
  UpdateDiscPayload,
  UpdateDiscResponse,
} from '@/lib/api/company/chartScan'
import { ChartEditorLayout } from './editor/ChartEditorLayout'
import { useChartT } from './i18n'

type Props = {
  companyId: string | number
  scan: ChartScanDetail
  initialDiscIndex?: number
  onSaveDisc: (discId: number, payload: UpdateDiscPayload) => Promise<UpdateDiscResponse>
  onDeleteDisc: (discId: number) => Promise<void>
  // Re-enter the pre-assign step from the editor toolbar (1:1 with WPF where
  // the import view's sidebar exposes Driver/Vehicle/Date for re-edit).
  onReassign?: (currentDiscIndex: number) => void
  onFinish: () => void
}

// Thin popup-step wrapper. The editor itself lives in ChartEditorLayout — this
// component only handles per-disc navigation + the on-finish hand-off back to
// the popup container.
export function StepReview({
  companyId,
  scan,
  initialDiscIndex = 0,
  onSaveDisc,
  onDeleteDisc,
  onReassign,
  onFinish,
}: Props) {
  const T = useChartT()
  const [index, setIndex] = React.useState(
    Math.min(Math.max(0, initialDiscIndex), Math.max(0, scan.discs.length - 1)),
  )
  const disc = scan.discs[index]

  if (!disc) {
    return (
      <div className="tw-p-6 tw-text-center">
        <p className="tw-text-slate-700">
          {T('noDiscsDetected', 'Nie wykryto tarczek na skanie.')}
        </p>
        <button
          className="tw-mt-4 tw-rounded tw-bg-slate-200 tw-px-4 tw-py-2"
          onClick={onFinish}
        >
          {T('close', 'Zamknij')}
        </button>
      </div>
    )
  }

  return (
    <ChartEditorLayout
      companyId={String(companyId)}
      disc={disc}
      onSave={(payload) => onSaveDisc(disc.discId, payload)}
      onDelete={async () => {
        await onDeleteDisc(disc.discId)
        if (index >= scan.discs.length - 1) onFinish()
      }}
      onCancel={onFinish}
      onPrev={index > 0 ? () => setIndex(index - 1) : undefined}
      onNext={index < scan.discs.length - 1 ? () => setIndex(index + 1) : onFinish}
      hasPrev={index > 0}
      hasNext={index < scan.discs.length - 1}
      discIndex={index}
      discCount={scan.discs.length}
      onReassign={onReassign ? () => onReassign(index) : undefined}
    />
  )
}
